import { Trans, t } from '@lingui/macro';
import { useState, useReducer, useEffect } from "react";
import { useHistory } from "../../hooks/useRouterCompat";
import { X, Copy, Save, Trash2, Download, Calendar, Lock, Loader2, ChevronUp } from 'lucide-react';
import { Room } from "../../models/Room";
import { UUID } from "../../models/local/Uid";
import { Fire } from "../../services/FireClient";
import { My } from "../../services/My";
import { ExternalShareLink, ExternalShareLinkDownload } from "../../models/ExternalShareLink";
import { RoomItem } from '../../models/RoomItem';
import { FileShareItem } from '../components/ItemFilePopover';
import { Util } from '../../services/Utils/Util';
import { UtilApp } from '../../services/Utils/UtilApp';
import { getDisplayName } from '../../services/Utils/userDisplayName';
import { copyToClipboard as clipboardWrite } from '../../services/Utils/ClipboardUtil';

import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import { useToast } from "../../components/ui/use-toast";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { Progress } from "../../components/ui/progress";
import { cn } from "../../lib/utils";
import { IconChevronDown, IconHash } from './FigmaIcons';

export const ShareLinkModal: React.FC<{
  show: boolean,
  isNew: boolean,
  setShow: (v: boolean) => void,
  link: ExternalShareLink,
  room: Room,
  downloads: ExternalShareLinkDownload[],
  items: RoomItem[],
  onDone: () => void
}> = ({ show, isNew, setShow, link, room, downloads, items, onDone }) => {
  const { toast } = useToast();
  const history = useHistory();
  const [inProgress, setInProgress] = useState(false);
  const [_, forceUpdate] = useReducer((x) => x + 1, 0);
  const [selectedOriginal, setSelectedOriginal] = useState<ExternalShareLink>();
  const [savingInProgress, setSavingInProgress] = useState(false);
  const [isExpired, setIsExpired] = useState(link.expireDate ? true : false);
  const [isDownloads, setIsDownloads] = useState(link.maxDownloads ? true : false);
  const [isPassword, setIsPassword] = useState(link.password ? true : false);
  const timezoneSeconds = -new Date().getTimezoneOffset() * 60;

  // Alert dialogs
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showDownloadsDialog, setShowDownloadsDialog] = useState(false);
  const [showSaveSuccessDialog, setShowSaveSuccessDialog] = useState(false);
  const [isShowUrl, setIsShowUrl] = useState(false);

  useEffect(() => {
    setSelectedOriginal(Util.pojo(link));
  }, []);

  async function copyToClipboard() {
    if (isNew || !Util.hasSameData(link, selectedOriginal)) {
      try {
        await saveOnly();
      } catch (e) {
        return; // handle error internally
      }
    }
    
    const text = Util.getPublicSecureShareURL(link.id);
    await clipboardWrite(text);
    toast({
      title: t`Copied to clipboard`,
      duration: 2000,
    });
  }

  function saveOnly(): Promise<void> {
    return new Promise((resolve, reject) => {
      // isNew일 경우 데이터가 같아도 강제 저장
      if (!isNew && Util.hasSameData(link, selectedOriginal)) {
        resolve();
        return;
      }
      setSavingInProgress(true);
      link.user = My.user;
      link.updated = new Date();
      Fire.item.saveExternalShareLink(link).then(() => {
        setSelectedOriginal(Util.pojo(link));
        setSavingInProgress(false);
        onDone();
        resolve();
      }).catch((e) => {
        setSavingInProgress(false);
        console.error('Failed to save share link', e);
        toast({
          title: t`Failed to create invitation link`,
          duration: 1500,
        });
        reject(e);
      });
    });
  }

  async function saveChanges() {
    if (!isNew && Util.hasSameData(link, selectedOriginal)) {
      toast({
        title: t`No changes detected`,
      });
    } else {
      setSavingInProgress(true);
      link.user = My.user;
      link.updated = new Date();
      await Fire.item.saveExternalShareLink(link);
      setSelectedOriginal(Util.pojo(link));
      setSavingInProgress(false);
      onDone();
      setShowSaveSuccessDialog(true);
    }
  }

  async function deleteLink() {
    if (link) {
      await Fire.item.deleteExternalShareLink(link);
      setShow(false);
    }
  }

  function showDownloadsInfo() {
    setShowDownloadsDialog(true);
  }

  function handleExpireDateChange(value: string) {
    if (value) {
      link.expireDate = new Date(value);
    }
  }

  function handleToggleExpired(checked: boolean) {
    setIsExpired(checked);
    if (checked && !link.expireDate) {
      link.expireDate = new Date(new Date().setHours(0, 0, 0, 0) + (7 * 24 * 60 * 60 + timezoneSeconds) * 1000);
    } else if (!checked) {
      link.expireDate = null;
    }
  }

  function handleToggleDownloads(checked: boolean) {
    setIsDownloads(checked);
    if (!checked) {
      link.maxDownloads = null;
    }
  }

  function handleTogglePassword(checked: boolean) {
    setIsPassword(checked);
    if (!checked) {
      link.password = null;
    }
  }

  const linkUrl = Util.getPublicSecureShareURL(link.id);
  const hasUnsavedChanges = !Util.hasSameData(link, selectedOriginal);

  const getDefaultExpireDate = () => {
    if (link.expireDate) {
      return new Date(new Date(link.expireDate).getTime() + timezoneSeconds * 1000).toISOString().slice(0, 16);
    }
    return new Date(new Date().setHours(0, 0, 0, 0) + (7 * 24 * 60 * 60 + timezoneSeconds) * 1000).toISOString().slice(0, 16);
  };

  return (
    <>
      <Dialog open={show} onOpenChange={setShow}>
        <DialogContent className="max-w-lg h-[85vh] md:h-auto md:max-h-[90vh] flex flex-col p-0 overflow-hidden">
          {/* 헤더 - 고정 */}
          <div className="p-6 pb-2">
            <DialogHeader>
              <div className="flex items-center justify-between">
                <DialogTitle>
                  {isNew ? <Trans>Create Share Link</Trans> : <Trans>Share links</Trans>}
                </DialogTitle>
                <div className="text-right text-xs text-muted-foreground mr-6">
                  <span className="font-medium text-primary">{getDisplayName(link.user)}</span>
                  <div>{UtilApp.dateStrToTimeAndDay(link.updated, My.user.lang)}</div>
                </div>
              </div>
            </DialogHeader>
          </div>

          {/* 프로그레스 바 */}
          {inProgress && <Progress className="mx-6 h-1" />}

          {/* 본문 - 스크롤 가능 */}
          <div className="flex-1 overflow-y-auto px-6 py-2 space-y-6">
            {/* 링크 URL 복사 영역 */}
            <div
              onClick={() => setIsShowUrl(!isShowUrl)}
              className="group bg-primary/20 hover:bg-primary/30 p-0 rounded-md text-body-lg text-primary cursor-pointer border border-primary/20 transition-all w-full overflow-hidden flex items-center justify-between"
            >
              <span className={`flex-1 min-w-0 px-3.5 py-2.5 leading-snug ${!isShowUrl ? 'truncate' : 'break-all whitespace-normal'}`}>
                {linkUrl}
              </span>
              <div className="flex items-center shrink-0 border-l border-primary/20 self-stretch px-3" onClick={(e) => { e.stopPropagation(); copyToClipboard(); }}>
                <div className="text-primary/70 group-hover:text-primary transition-colors flex items-center justify-center">
                  <Copy className="w-5 h-5" />
                </div>
              </div>
            </div>

            {/* 설정 보관 박스 */}
            <div className="bg-[rgb(var(--message-bubble))] p-4 rounded-xl flex flex-col gap-4">
              
              {/* 파일 목록 */}
              <div className="flex flex-col gap-1.5">
                <div className="flex items-center justify-between">
                  <span className="text-body font-medium text-[color:rgb(var(--msg-icon))]"><Trans>Files</Trans></span>
                  {downloads.length > 0 && (
                    <button
                      onClick={showDownloadsInfo}
                      className="text-xs text-primary hover:text-primary hover:underline"
                    >
                      <Trans>{downloads.length} downloads</Trans>
                    </button>
                  )}
                </div>
                <div className="flex flex-col gap-1">
                  {link.roomItems.map(item => (
                    <FileShareItem
                      key={item.id}
                      item={item}
                      selectedItems={link.roomItems}
                      downloads={downloads.filter(d => d.itemId == item.id)}
                      checkable={false}
                      remove={() => forceUpdate()}
                    />
                  ))}
                  {link.roomItems.length === 0 && (
                    <p className="text-sm text-destructive text-center py-2">
                      <Trans>No files added.</Trans>
                    </p>
                  )}
                </div>
              </div>

              <div className="border-t border-border w-full" />

              {/* 제목 */}
              <div className="flex flex-col gap-1.5">
                <span className="text-body font-medium text-[color:rgb(var(--msg-icon))]">{t`Title`}</span>
                <div className="bg-background border border-border h-11 rounded-lg flex items-center px-3">
                  <Input
                    placeholder={t`Optional title`}
                    value={link.title || ''}
                    onChange={(e) => { link.title = e.target.value; forceUpdate(); }}
                    maxLength={35}
                    className="border-0 bg-transparent h-full px-0 shadow-none text-body-lg text-[color:rgb(var(--text-body))] placeholder:text-[color:rgb(var(--tbl-text-secondary))] focus-visible:ring-0"
                  />
                </div>
              </div>

              <div className="border-t border-border w-full" />

              {/* 만료일 */}
              <div className="flex flex-col gap-1.5">
                <div className="flex items-center justify-between">
                   <span className="text-body font-medium text-[color:rgb(var(--msg-icon))]">{t`Expire date`}</span>
                   <Switch checked={isExpired} onCheckedChange={handleToggleExpired} />
                </div>
                {isExpired && (
                  <div className="bg-background border border-border h-11 rounded-lg flex items-center px-3 gap-2 mt-1">
                    <Calendar className="w-4 h-4 text-muted-foreground shrink-0" />
                    <Input
                      type="datetime-local"
                      value={getDefaultExpireDate()}
                      onChange={(e) => handleExpireDateChange(e.target.value)}
                      className="border-0 bg-transparent h-full px-0 shadow-none text-body-lg text-[color:rgb(var(--tbl-text-secondary))] focus-visible:ring-0"
                    />
                  </div>
                )}
              </div>

              <div className="border-t border-border w-full" />

              {/* 최대 다운로드 수 */}
              <div className="flex flex-col gap-1.5">
                <div className="flex items-center justify-between">
                   <span className="text-body font-medium text-[color:rgb(var(--msg-icon))]">{t`Max downloads per file`}</span>
                   <Switch checked={isDownloads} onCheckedChange={handleToggleDownloads} />
                </div>
                {isDownloads && (
                  <div className="bg-background border border-border h-11 rounded-lg flex items-center px-3 gap-2 mt-1">
                    <Download className="w-4 h-4 text-muted-foreground shrink-0" />
                    <Input
                      type="number"
                      placeholder={t`Number`}
                      value={link.maxDownloads || ''}
                      onChange={(e) => { link.maxDownloads = e.target.value ? parseInt(e.target.value) : null; forceUpdate(); }}
                      className="border-0 bg-transparent h-full px-0 shadow-none text-body-lg text-[color:rgb(var(--text-body))] placeholder:text-[color:rgb(var(--tbl-text-secondary))] focus-visible:ring-0"
                    />
                  </div>
                )}
              </div>

              <div className="border-t border-border w-full" />

              {/* 비밀번호 */}
              <div className="flex flex-col gap-1.5">
                <div className="flex items-center justify-between">
                   <span className="text-body font-medium text-[color:rgb(var(--msg-icon))]">{t`Password`}</span>
                   <Switch checked={isPassword} onCheckedChange={handleTogglePassword} />
                </div>
                {isPassword && (
                  <div className="bg-background border border-border h-11 rounded-lg flex items-center px-3 gap-2 mt-1">
                    <Lock className="w-4 h-4 text-muted-foreground shrink-0" />
                    <Input
                      type="text"
                      placeholder={t`Optional password`}
                      value={link.password || ''}
                      onChange={(e) => { link.password = e.target.value; forceUpdate(); }}
                      className="border-0 bg-transparent h-full px-0 shadow-none text-body-lg text-[color:rgb(var(--text-body))] placeholder:text-[color:rgb(var(--tbl-text-secondary))] focus-visible:ring-0"
                    />
                  </div>
                )}
              </div>

            </div>

          </div>
          {/* 푸터 (버튼 영역) - 고정 (위쪽 여백 보정) */}
          <div className="px-6 pb-6 pt-4 shrink-0">
            {savingInProgress && (
              <div className="flex items-center justify-center py-2 h-11">
                <Loader2 className="w-5 h-5 animate-spin text-primary" />
              </div>
            )}
            {!savingInProgress && (
              <div className="flex flex-col gap-3">
                {hasUnsavedChanges && (
                  <p className="text-xs text-center text-[color:rgb(var(--tbl-text-secondary))]">
                    <Trans>Remember to save your changes.</Trans>
                  </p>
                )}
                <div className="flex gap-3">
                  <Button
                    onClick={saveChanges}
                    className="flex-1 rounded-control h-11 text-body font-semibold bg-primary hover:bg-primary/90 text-primary-foreground shadow-none"
                  >
                    {isNew ? <Trans>Save</Trans> : <Trans>Save changes</Trans>}
                  </Button>
                  {!isNew && (
                    <Button
                      variant="outline"
                      onClick={() => setShowDeleteDialog(true)}
                      className="w-[160px] rounded-control h-11 text-body font-semibold border border-solid border-[color:rgb(var(--border))] bg-background text-[color:rgb(var(--msg-icon-hover))] hover:bg-muted/50 hover:text-[color:rgb(var(--text-body))] shadow-none"
                    >
                      <Trans>Delete</Trans>
                    </Button>
                  )}
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* 삭제 확인 다이얼로그 */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t`Delete link`}</AlertDialogTitle>
            <AlertDialogDescription>
              {t`Are you sure you want to delete this link?`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t`Cancel`}</AlertDialogCancel>
            <AlertDialogAction onClick={deleteLink} className="bg-destructive hover:bg-destructive/90">
              {t`Delete`}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* 다운로드 정보 다이얼로그 */}
      <AlertDialog open={showDownloadsDialog} onOpenChange={setShowDownloadsDialog}>
        <AlertDialogContent className="max-h-[80vh] overflow-y-auto">
          <AlertDialogHeader>
            <AlertDialogTitle>{t`Recent downloads`}</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 pt-2">
                {downloads.length > 0 ? (
                  downloads.map((d, index) => {
                    const user = d.userId ? My.getUserFromRooms(d.userId)?.name ?? t`UserId: ${d.userId}` : t`Anonymous (ip: ${d.ip})`;
                    return (
                      <div key={index} className="p-2 bg-muted/50 rounded-lg text-sm">
                        <p className="font-medium text-foreground">{d.filename}</p>
                        <p className="text-muted-foreground">{user}, {UtilApp.dateStrToTimeAndDay(d.created, My.user.lang)}</p>
                      </div>
                    );
                  })
                ) : (
                  <p className="text-muted-foreground">{t`No downloads yet`}</p>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction>{t`OK`}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* 저장 성공 다이얼로그 */}
      <AlertDialog open={showSaveSuccessDialog} onOpenChange={setShowSaveSuccessDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t`Successfully saved`}</AlertDialogTitle>
            <AlertDialogDescription>
              {t`Would you like to copy the link to clipboard?`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t`Close`}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                const text = Util.getPublicSecureShareURL(link.id);
                clipboardWrite(text);
                toast({ title: t`Copied to clipboard`, duration: 2000 });
              }}
              className="bg-primary hover:bg-primary/90"
            >
              {t`Copy`}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>


    </>
  );
}

export default ShareLinkModal;